import Colors from "../../../Resources/Color";

const PAGE_WIDTH = 527;
const PAGE_MARGINS = [34, 84, 34, 56];
const HEADER_MARGIN = [34, 24, 34, 0];
const FOOTER_MARGIN = [34, 0, 34, 18];
const HEADER_LOGO_BOX_WIDTH = 84;
const HEADER_LOGO_BOX_HEIGHT = 28;
const MEAL_CARD_HORIZONTAL_PADDING = 0;
const MEAL_CARD_CONTENT_WIDTH = PAGE_WIDTH - MEAL_CARD_HORIZONTAL_PADDING * 2;
const CHOICE_GROUP_HORIZONTAL_PADDING = 10;
const CHOICE_GROUP_VERTICAL_PADDING = 6;
const MEAL_OPTION_GRID_COLUMN_GAP = 18;
const MEAL_OPTION_COLUMN_WIDTH =
  (MEAL_CARD_CONTENT_WIDTH - MEAL_OPTION_GRID_COLUMN_GAP) / 2;
const MEAL_CARD_MARGIN_BOTTOM = 14;
const MEAL_CONTENT_MARGIN_BOTTOM = 0;
const LATER_PLAN_SECTION_TOP_MARGIN = 12;
const PDF_BULLET_COLUMN_WIDTH = 10;
const PDF_BULLET_CANVAS_SIZE = 8;
const PDF_BULLET_TOP_INSET = 3;
const PDF_BULLET_FILLED_RADIUS = 1.2;
const PDF_BULLET_OUTLINE_RADIUS = 1.55;
const PDF_BULLET_OUTLINE_WIDTH = 0.6;
const PDF_SOFT_WRAP_CHARACTER = "\u200B";
const PDF_SOFT_WRAP_INTERVAL = 12;
const DIVIDER_COLOR = blendColorOverWhite(Colors.bubbleColor, 0.14);
const MEAL_CARD_BACKGROUND = "#FFFFFF";
const MEAL_CARD_BORDER_COLOR = blendColorOverWhite(Colors.bubbleColor, 0.2);
const MEAL_HEADER_BACKGROUND = blendColorOverWhite(Colors.bubbleColor, 0.05);
const PDF_ICON_GLYPHS = {
  cafeOutline: String.fromCodePoint(60107),
  restaurantOutline: String.fromCodePoint(60983),
  moonOutline: String.fromCodePoint(60770),
  partlySunnyOutline: String.fromCodePoint(60818),
  fastFoodOutline: String.fromCodePoint(60401),
  nutritionOutline: String.fromCodePoint(60806),
};

function isSpanishLanguage(language = "en") {
  return String(language ?? "").toLowerCase().startsWith("es");
}

function getPdfMetadataFallbackTitle(snapshot = null, { plural = false } = {}) {
  if (plural) {
    return snapshot?.pdfMetadataMultiplePlansTitle ||
      (isSpanishLanguage(snapshot?.language) ? "Planes nutricionales" : "Nutrition plans");
  }

  return snapshot?.pdfMetadataSinglePlanTitle ||
    (isSpanishLanguage(snapshot?.language) ? "Plan nutricional" : "Nutrition plan");
}

function hexToRgb(hexColor) {
  const normalizedHex = String(hexColor).replace("#", "");

  if (normalizedHex.length !== 6) {
    return null;
  }

  return {
    r: Number.parseInt(normalizedHex.slice(0, 2), 16),
    g: Number.parseInt(normalizedHex.slice(2, 4), 16),
    b: Number.parseInt(normalizedHex.slice(4, 6), 16),
  };
}

function blendColorOverWhite(hexColor, alpha = 0.12) {
  const rgb = hexToRgb(hexColor);

  if (!rgb) {
    return "#E8EFF1";
  }

  const blendChannel = (channel) =>
    Math.round(255 - (255 - channel) * Math.max(0, Math.min(alpha, 1)));
  const toHex = (channel) => blendChannel(channel).toString(16).padStart(2, "0");

  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
}

const ICON_BADGE_BACKGROUND = blendColorOverWhite(Colors.bubbleColor, 0.12);

function buildBadgeCanvas({
  size = 28,
  backgroundColor = ICON_BADGE_BACKGROUND,
  cornerRadius = 9,
} = {}) {
  const safeCornerRadius = Math.min(cornerRadius, size / 2);
  const innerWidth = Math.max(0, size - safeCornerRadius * 2);
  const innerHeight = Math.max(0, size - safeCornerRadius * 2);

  return [
    {
      type: "rect",
      x: safeCornerRadius,
      y: 0,
      w: innerWidth,
      h: size,
      color: backgroundColor,
      lineColor: backgroundColor,
    },
    {
      type: "rect",
      x: 0,
      y: safeCornerRadius,
      w: size,
      h: innerHeight,
      color: backgroundColor,
      lineColor: backgroundColor,
    },
    {
      type: "ellipse",
      x: safeCornerRadius,
      y: safeCornerRadius,
      r1: safeCornerRadius,
      r2: safeCornerRadius,
      color: backgroundColor,
      lineColor: backgroundColor,
    },
    {
      type: "ellipse",
      x: size - safeCornerRadius,
      y: safeCornerRadius,
      r1: safeCornerRadius,
      r2: safeCornerRadius,
      color: backgroundColor,
      lineColor: backgroundColor,
    },
    {
      type: "ellipse",
      x: safeCornerRadius,
      y: size - safeCornerRadius,
      r1: safeCornerRadius,
      r2: safeCornerRadius,
      color: backgroundColor,
      lineColor: backgroundColor,
    },
    {
      type: "ellipse",
      x: size - safeCornerRadius,
      y: size - safeCornerRadius,
      r1: safeCornerRadius,
      r2: safeCornerRadius,
      color: backgroundColor,
      lineColor: backgroundColor,
    },
  ];
}

function buildBulletMarker({ variant = "filled" } = {}) {
  const isOutline = variant === "outline";
  const center = PDF_BULLET_CANVAS_SIZE / 2;
  const radius = isOutline ? PDF_BULLET_OUTLINE_RADIUS : PDF_BULLET_FILLED_RADIUS;
  const marker = {
    type: "ellipse",
    x: center,
    y: center,
    r1: radius,
    r2: radius,
    lineColor: Colors.bubbleColor,
  };

  if (isOutline) {
    marker.lineWidth = PDF_BULLET_OUTLINE_WIDTH;
  } else {
    marker.color = Colors.bubbleColor;
  }

  return {
    canvas: [marker],
    alignment: "center",
    verticalAlignment: "top",
    margin: [0, PDF_BULLET_TOP_INSET, 0, 0],
  };
}

function normalizeIconLabel(value = "") {
  return String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function normalizePdfWrapText(value = "") {
  const text = String(value ?? "");

  if (!text) {
    return "";
  }

  return text.replace(/[^\s\-_/.,()[\]{}]{13,}/g, (segment) => {
    let normalizedSegment = "";

    for (let index = 0; index < segment.length; index += 1) {
      if (index > 0 && index % PDF_SOFT_WRAP_INTERVAL === 0) {
        normalizedSegment += PDF_SOFT_WRAP_CHARACTER;
      }

      normalizedSegment += segment[index];
    }

    return normalizedSegment;
  });
}

function getMealIconGlyph(mealName = "") {
  const normalizedMealName = normalizeIconLabel(mealName);

  if (
    normalizedMealName.includes("breakfast") ||
    normalizedMealName.includes("desayuno")
  ) {
    return PDF_ICON_GLYPHS.cafeOutline;
  }

  if (
    normalizedMealName.includes("snack") ||
    normalizedMealName.includes("merienda")
  ) {
    return PDF_ICON_GLYPHS.nutritionOutline;
  }

  if (
    normalizedMealName.includes("dinner") ||
    normalizedMealName.includes("cena") ||
    normalizedMealName.includes("night")
  ) {
    return PDF_ICON_GLYPHS.moonOutline;
  }

  if (
    normalizedMealName.includes("lunch") ||
    normalizedMealName.includes("almuerzo") ||
    normalizedMealName.includes("comida") ||
    normalizedMealName.includes("afternoon")
  ) {
    return PDF_ICON_GLYPHS.partlySunnyOutline;
  }

  if (
    normalizedMealName.includes("pre") ||
    normalizedMealName.includes("post") ||
    normalizedMealName.includes("workout")
  ) {
    return PDF_ICON_GLYPHS.nutritionOutline;
  }

  return PDF_ICON_GLYPHS.restaurantOutline;
}

function buildIconBadge({
  iconGlyph = PDF_ICON_GLYPHS.restaurantOutline,
  iconFontFamily = null,
  size = 28,
  fontSize = 13,
  fallbackText = "",
} = {}) {
  const badgeText = iconFontFamily
    ? {
        text: iconGlyph,
        font: iconFontFamily,
        fontSize,
        color: Colors.bubbleColor,
        alignment: "center",
        verticalAlignment: "middle",
        lineHeight: 1,
        noWrap: true,
        margin: [0, 1, 0, 0],
      }
    : {
        text: (fallbackText || "•").slice(0, 1).toUpperCase(),
        alignment: "center",
        verticalAlignment: "middle",
        lineHeight: 1,
        fontSize: Math.max(10, fontSize - 2),
        bold: true,
        color: Colors.bubbleColor,
        noWrap: true,
      };

  return {
    width: size,
    height: size,
    stack: [
      {
        canvas: buildBadgeCanvas({
          size,
        }),
      },
      {
        margin: [0, -size, 0, 0],
        table: {
          widths: [size],
          heights: [size],
          body: [[badgeText]],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
      },
    ],
  };
}

function buildDividerLine(lineColor = DIVIDER_COLOR, width = PAGE_WIDTH) {
  return {
    canvas: [
      {
        type: "line",
        x1: 0,
        y1: 0,
        x2: width,
        y2: 0,
        lineWidth: 1,
        lineColor,
      },
    ],
  };
}

function buildQuarterArcPoints(
  centerX,
  centerY,
  radius,
  startAngleDegrees,
  endAngleDegrees,
  steps = 6,
) {
  const points = [];

  for (let index = 0; index <= steps; index += 1) {
    const progress = index / steps;
    const angleDegrees =
      startAngleDegrees + (endAngleDegrees - startAngleDegrees) * progress;
    const angleRadians = (angleDegrees * Math.PI) / 180;

    points.push({
      x: Number((centerX + Math.cos(angleRadians) * radius).toFixed(2)),
      y: Number((centerY + Math.sin(angleRadians) * radius).toFixed(2)),
    });
  }

  return points;
}

function buildMealCardCornerCanvas(corner = "top_left", { outlineOnly = false } = {}) {
  const size = MEAL_CARD_CORNER_RADIUS;

  const filledCorner = (points) => ({
    type: "polyline",
    closePath: true,
    color: MEAL_CARD_BACKGROUND,
    lineColor: MEAL_CARD_BACKGROUND,
    points,
  });

  const outlinedCorner = (points) => ({
    type: "polyline",
    closePath: false,
    lineColor: MEAL_CARD_BORDER_COLOR,
    lineWidth: 1,
    points,
  });

  switch (corner) {
    case "top_left":
      return [
        outlineOnly
          ? outlinedCorner(buildQuarterArcPoints(size, size, size, 270, 180))
          : filledCorner([
              { x: size, y: 0 },
              { x: size, y: size },
              { x: 0, y: size },
              ...buildQuarterArcPoints(size, size, size, 180, 270).slice(1),
            ]),
      ];
    case "top_right":
      return [
        outlineOnly
          ? outlinedCorner(buildQuarterArcPoints(0, size, size, 270, 360))
          : filledCorner([
              { x: 0, y: 0 },
              { x: 0, y: size },
              { x: size, y: size },
              ...buildQuarterArcPoints(0, size, size, 0, -90).slice(1),
            ]),
      ];
    case "bottom_left":
      return [
        outlineOnly
          ? outlinedCorner(buildQuarterArcPoints(size, 0, size, 180, 90))
          : filledCorner([
              { x: 0, y: 0 },
              { x: size, y: 0 },
              { x: size, y: size },
              ...buildQuarterArcPoints(size, 0, size, 90, 180).slice(1),
            ]),
      ];
    case "bottom_right":
    default:
      return [
        outlineOnly
          ? outlinedCorner(buildQuarterArcPoints(0, 0, size, 0, 90))
          : filledCorner([
              { x: 0, y: 0 },
              { x: size, y: 0 },
              { x: 0, y: size },
              ...buildQuarterArcPoints(0, 0, size, 0, 90).slice(1),
            ]),
      ];
  }
}

function buildMealCardCornerLayer(corner = "top_left") {
  return [
    ...buildMealCardCornerCanvas(corner, { outlineOnly: true }),
  ];
}

function resolvePdfCoachName(snapshot) {
  return String(snapshot?.branding?.coachName || "Efort").trim() || "Efort";
}

function resolvePdfAthleteName(snapshot) {
  return String(snapshot?.athleteName || "").trim();
}

function buildHeaderIdentityLine(label, value) {
  return {
    text: [
      {
        text: `${label} `,
        style: "headerIdentityLabel",
      },
      {
        text: value,
        style: "headerIdentityValue",
      },
    ],
    alignment: "center",
    noWrap: true,
  };
}

function buildHeaderIdentityContent(snapshot) {
  const coachName = resolvePdfCoachName(snapshot);
  const athleteName = resolvePdfAthleteName(snapshot);
  const lines = [buildHeaderIdentityLine("Coach:", coachName)];

  if (athleteName) {
    lines.push({
      ...buildHeaderIdentityLine("Athlete:", athleteName),
      margin: [0, 2, 0, 0],
    });
  }

  return {
    stack: lines,
    alignment: "center",
    margin: athleteName ? [0, 0, 0, 0] : [0, 1, 0, 0],
  };
}

function buildHeader(snapshot, assets = {}) {
  return {
    margin: HEADER_MARGIN,
    table: {
      widths: [HEADER_LOGO_BOX_WIDTH, "*", HEADER_LOGO_BOX_WIDTH],
      heights: [HEADER_LOGO_BOX_HEIGHT],
      body: [
        [
          {
            ...(assets?.coachLogoImage
              ? {
                  image: "coachLogo",
                  fit: [HEADER_LOGO_BOX_WIDTH, HEADER_LOGO_BOX_HEIGHT],
                  alignment: "left",
                }
              : {
                  text: "",
                }),
            verticalAlignment: "middle",
          },
          {
            ...buildHeaderIdentityContent(snapshot),
            alignment: "center",
            verticalAlignment: "middle",
          },
          {
            ...(assets?.efortLogoImage
              ? {
                  image: "efortLogo",
                  fit: [HEADER_LOGO_BOX_WIDTH, HEADER_LOGO_BOX_HEIGHT],
                  alignment: "right",
                }
              : {
                  columns: [
                    {
                      width: 22,
                      canvas: buildBadgeCanvas({
                        size: 22,
                        backgroundColor: "#EEF6F8",
                      }),
                      margin: [0, 0, 8, 0],
                    },
                    {
                      width: "auto",
                      text: "EFORT",
                      style: "efortWordmark",
                      margin: [0, 3, 0, 0],
                    },
                  ],
                  columnGap: 0,
                }),
            alignment: "right",
            verticalAlignment: "middle",
          },
        ],
      ],
    },
    layout: {
      hLineWidth: () => 0,
      vLineWidth: () => 0,
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 0,
      paddingBottom: () => 0,
    },
  };
}

function buildFooter(snapshot, currentPage, pageCount) {
  const exportDateLabel = String(snapshot?.exportDateLabel || "").trim();

  return {
    margin: FOOTER_MARGIN,
    stack: [
      buildDividerLine(),
      {
        columns: [
          {
            width: "*",
            text: "EFORT",
            style: "footerText",
            margin: [0, 8, 0, 0],
          },
          {
            width: "auto",
            text: exportDateLabel,
            style: "footerTextCenter",
            margin: [0, 8, 0, 0],
          },
          {
            width: "*",
            text: `${currentPage} / ${pageCount}`,
            style: "footerTextRight",
            margin: [0, 8, 0, 0],
          },
        ],
      },
    ],
  };
}

function buildInlinePlanTitleBlock(plan = {}) {
  return buildPlanTitleBlock(plan);
}

function buildPlanTitleBlock(plan = {}) {
  const titleText = String(plan?.title || "");
  const content = [
    {
      text: titleText,
      style: "documentTitle",
      alignment: "center",
      margin: [0, 0, 0, plan?.notes ? 6 : 12],
    },
  ];

  if (plan?.notes) {
    content.push(
      {
        text: plan.notes,
        style: "notesText",
        margin: [0, 0, 0, 16],
      },
    );
  }

  return {
    stack: content,
  };
}

function buildFoodRowDefinition(
  block,
  {
    nameStyle = "foodName",
    quantityStyle = "foodQuantityInline",
    bulletVariant = "filled",
    indentLeft = 0,
    marginTop = 0,
    bottomMargin = 4,
  } = {},
) {
  const hasQuantity = Boolean(block?.quantityLabel);
  const hasLink = typeof block?.link === "string" && block.link.trim().length > 0;
  const normalizedName = normalizePdfWrapText(block?.name || "");
  const nameCell = hasLink
    ? {
        text: normalizedName,
        style: nameStyle,
        color: Colors.blackColor,
        decoration: "underline",
        decorationColor: Colors.blackColor,
        link: block.link.trim(),
        verticalAlignment: "middle",
      }
    : {
        text: normalizedName,
        style: nameStyle,
        verticalAlignment: "middle",
      };

  return {
    unbreakable: true,
    table: {
      dontBreakRows: true,
      widths: [PDF_BULLET_COLUMN_WIDTH, "auto", "*"],
      body: [
        [
          buildBulletMarker({
            variant: bulletVariant,
          }),
          {
            text: hasQuantity ? block.quantityLabel : "",
            style: quantityStyle,
            noWrap: true,
            verticalAlignment: "middle",
          },
          nameCell,
        ],
      ],
    },
    layout: {
      hLineWidth: () => 0,
      vLineWidth: () => 0,
      paddingLeft: () => 0,
      paddingRight: (columnIndex) => {
        if (columnIndex === 0) {
          return 6;
        }

        if (columnIndex === 1 && hasQuantity) {
          return 6;
        }

        return 0;
      },
      paddingTop: () => 0,
      paddingBottom: () => 0,
    },
    margin: [indentLeft, marginTop, 0, bottomMargin],
  };
}

function buildChoiceGroupDefinition(block) {
  const titleRow = buildFoodRowDefinition(
    {
      name: block?.title || "",
      quantityLabel: "",
    },
    {
      nameStyle: "foodName",
      bulletVariant: "filled",
      bottomMargin: 4,
    },
  );

  const rows = (Array.isArray(block?.options) ? block.options : []).map((option) =>
    buildFoodRowDefinition(
      option,
      {
        bulletVariant: "outline",
        nameStyle: "choiceOptionName",
        quantityStyle: "choiceOptionQuantity",
        indentLeft: 16,
        bottomMargin: 1,
      },
    ),
  );

  return {
    stack: [
      titleRow,
      ...rows,
    ],
    margin: [0, 0, 0, CHOICE_GROUP_VERTICAL_PADDING],
  };
}

function buildOptionBlocks(blocks = []) {
  return blocks.map((block) =>
    block?.kind === "choice_group"
      ? buildChoiceGroupDefinition(block)
      : buildFoodRowDefinition(block),
  );
}

function buildOptionDefinition(option, { bottomMargin = 6 } = {}) {
  const blockStack = buildOptionBlocks(option?.blocks);
  if (blockStack.length === 0) {
    return null;
  }

  const showTitle = Boolean(option?.showTitle);
  const titleNode = showTitle
    ? {
        text: normalizePdfWrapText(option?.name || ""),
        style: "optionTitle",
        margin: [0, 0, 0, 4],
      }
    : null;
  const baseTopMargin = showTitle ? 2 : 0;

  return {
    stack: [
      ...(titleNode ? [titleNode] : []),
      ...blockStack,
    ],
    margin: [0, baseTopMargin, 0, bottomMargin],
  };
}

function chunkIntoPairs(items = []) {
  const pairs = [];

  for (let index = 0; index < items.length; index += 2) {
    pairs.push(items.slice(index, index + 2));
  }

  return pairs;
}

function buildOptionPairColumns(leftNode, rightNode, { unbreakable = false } = {}) {
  return {
    columns: [
      leftNode
        ? {
            width: MEAL_OPTION_COLUMN_WIDTH,
            ...leftNode,
          }
        : {
            width: MEAL_OPTION_COLUMN_WIDTH,
            text: "",
          },
      rightNode
        ? {
            width: MEAL_OPTION_COLUMN_WIDTH,
            ...rightNode,
          }
        : {
            width: MEAL_OPTION_COLUMN_WIDTH,
            text: "",
          },
    ],
    columnGap: MEAL_OPTION_GRID_COLUMN_GAP,
    unbreakable,
  };
}

function buildMealOptionSections(options = [], { keepTogether = true } = {}) {
  const normalizedOptions = Array.isArray(options) ? options : [];

  if (normalizedOptions.length <= 1) {
    const optionDefinition = buildOptionDefinition(normalizedOptions[0], {
      bottomMargin: 4,
    });
    return {
      firstLead: optionDefinition,
      trailingSections: [],
    };
  }

  const optionPairs = chunkIntoPairs(normalizedOptions).map((pair) =>
    pair.map((option) =>
      buildOptionDefinition(option, {
        bottomMargin: 0,
      }),
    ),
  );
  const [firstPair, ...remainingPairs] = optionPairs;
  const trailingSections = remainingPairs.map((pair) =>
    buildOptionPairColumns(pair?.[0], pair?.[1], {
      unbreakable: keepTogether,
    }),
  );

  return {
    firstLead: buildOptionPairColumns(firstPair?.[0], firstPair?.[1], {
      unbreakable: keepTogether,
    }),
    trailingSections,
  };
}

function buildMealHeaderDefinition(meal) {
  return {
    table: {
      widths: ["*"],
      body: [
        [
          {
            stack: [
              {
                table: {
                  widths: [28, "auto"],
                  body: [
                    [
                      {
                        ...buildIconBadge({
                          iconGlyph: getMealIconGlyph(meal?.name),
                          iconFontFamily: meal?.iconFontFamily,
                          fallbackText: meal?.name || "M",
                        }),
                        verticalAlignment: "middle",
                      },
                      {
                        text: meal?.name || "",
                        style: "mealTitle",
                        alignment: "center",
                        verticalAlignment: "middle",
                        margin: [10, 3, 0, 0],
                      },
                    ],
                  ],
                },
                layout: {
                  hLineWidth: () => 0,
                  vLineWidth: () => 0,
                  paddingLeft: () => 0,
                  paddingRight: () => 0,
                  paddingTop: () => 0,
                  paddingBottom: () => 0,
                },
                alignment: "center",
              },
            ],
            fillColor: MEAL_HEADER_BACKGROUND,
            border: [false, false, false, false],
          },
        ],
      ],
    },
    layout: {
      defaultBorder: false,
      hLineWidth: () => 0,
      vLineWidth: () => 0,
      paddingLeft: () => 14,
      paddingRight: () => 14,
      paddingTop: () => 6,
      paddingBottom: () => 6,
    },
    margin: [0, 0, 0, 8],
  };
}

function buildMealCardSections(meal) {
  const keepTogether = meal?.keepTogether !== false;
  const optionSections = buildMealOptionSections(meal?.options, {
    keepTogether,
  });
  const trailingSections = Array.isArray(optionSections?.trailingSections)
    ? [...optionSections.trailingSections]
    : [];
  const leadSection = optionSections?.firstLead
    ? {
        stack: [
          buildMealHeaderDefinition(meal),
          optionSections.firstLead,
        ],
        unbreakable: keepTogether,
      }
    : buildMealHeaderDefinition(meal);

  if (meal?.notes) {
    trailingSections.push({
      stack: [
        buildDividerLine(DIVIDER_COLOR, MEAL_CARD_CONTENT_WIDTH),
        {
          text: meal.notes,
          style: "notesText",
          margin: [0, 8, 0, 0],
        },
      ],
      margin: [0, 2, 0, 0],
      unbreakable: keepTogether,
    });
  }

  return {
    leadSection,
    trailingSections,
  };
}

function buildMealCardDefinition(
  meal,
  { bottomMargin = MEAL_CARD_MARGIN_BOTTOM } = {},
) {
  const { leadSection, trailingSections } = buildMealCardSections(meal);

  return {
    stack: [
      leadSection,
      ...trailingSections,
    ],
    margin: [0, 0, 0, bottomMargin],
  };
}

function buildMealContent(
  meal,
  mealIndex,
  {
    cardBottomMargin = MEAL_CARD_MARGIN_BOTTOM,
    contentBottomMargin = MEAL_CONTENT_MARGIN_BOTTOM,
  } = {},
) {
  return {
    id:
      meal?.keepTogether === false
        ? meal?.id
          ? `meal-card-split-${meal.id}`
          : `meal-card-split-${mealIndex}`
        : meal?.id
          ? `meal-card-keep-${meal.id}`
          : `meal-card-keep-${mealIndex}`,
    stack: [
      buildMealCardDefinition(meal, {
        bottomMargin: cardBottomMargin,
      }),
    ],
    margin: [0, 0, 0, contentBottomMargin],
    unbreakable: meal?.keepTogether !== false,
  };
}

function buildLaterPlanIntroContent(snapshot, sectionIndex = 0) {
  const meals = Array.isArray(snapshot?.plan?.meals) ? snapshot.plan.meals : [];
  const firstMeal = meals[0];
  const remainingMeals = meals.slice(1);
  const content = [];

  if (!firstMeal) {
    return [
      {
        id: `plan-section-intro-${sectionIndex}`,
        stack: [buildInlinePlanTitleBlock(snapshot?.plan)],
        margin: [0, LATER_PLAN_SECTION_TOP_MARGIN, 0, 0],
        unbreakable: true,
      },
    ];
  }

  const firstMealSections = buildMealCardSections(firstMeal);
  const hasRemainingMeals = remainingMeals.length > 0;
  const firstMealBottomMargin = hasRemainingMeals ? MEAL_CARD_MARGIN_BOTTOM : 0;
  const firstMealKeepTogether = firstMeal?.keepTogether !== false;

  content.push({
    id: `plan-section-intro-${sectionIndex}`,
    stack: [buildInlinePlanTitleBlock(snapshot?.plan)],
    margin: [0, LATER_PLAN_SECTION_TOP_MARGIN, 0, 0],
    unbreakable: true,
  });

  if (!firstMealKeepTogether) {
    content.push(
      buildMealContent(firstMeal, `${sectionIndex}-0`, {
        cardBottomMargin: hasRemainingMeals ? MEAL_CARD_MARGIN_BOTTOM : 0,
        contentBottomMargin: hasRemainingMeals ? MEAL_CONTENT_MARGIN_BOTTOM : 0,
      }),
    );
  } else {
    content[0] = {
      ...content[0],
      stack: [
        buildInlinePlanTitleBlock(snapshot?.plan),
        {
          stack: [firstMealSections.leadSection],
          margin: [
            0,
            0,
            0,
            firstMealSections.trailingSections.length === 0 ? firstMealBottomMargin : 0,
          ],
        },
      ],
    };
  }

  if (firstMealKeepTogether && firstMealSections.trailingSections.length > 0) {
    content.push({
      stack: firstMealSections.trailingSections,
      margin: [0, 0, 0, firstMealKeepTogether && hasRemainingMeals ? MEAL_CARD_MARGIN_BOTTOM : 0],
    });
  }

  remainingMeals.forEach((meal, mealIndex) => {
    const isLastMealInPlan = mealIndex === remainingMeals.length - 1;
    content.push(
      buildMealContent(meal, `${sectionIndex}-${mealIndex + 1}`, {
        cardBottomMargin: isLastMealInPlan ? 0 : MEAL_CARD_MARGIN_BOTTOM,
        contentBottomMargin: isLastMealInPlan ? 0 : MEAL_CONTENT_MARGIN_BOTTOM,
      }),
    );
  });

  return content;
}

function buildSinglePlanContent(snapshot, assets = {}) {
  const meals = Array.isArray(snapshot?.plan?.meals) ? snapshot.plan.meals : [];

  return [
    buildPlanTitleBlock(snapshot?.plan),
    ...meals.map((meal, index) =>
      buildMealContent(
        {
          ...meal,
          iconFontFamily: assets?.iconFontFamily,
        },
        index,
        {
          cardBottomMargin:
            index === meals.length - 1 ? 0 : MEAL_CARD_MARGIN_BOTTOM,
          contentBottomMargin:
            index === meals.length - 1 ? 0 : MEAL_CONTENT_MARGIN_BOTTOM,
        },
      ),
    ),
  ];
}

function buildCombinedPlanContent(snapshots, assets = {}) {
  const normalizedSnapshots = (Array.isArray(snapshots) ? snapshots : []).filter(Boolean);
  if (normalizedSnapshots.length === 0) {
    return [];
  }

  return normalizedSnapshots.flatMap((snapshot, sectionIndex) => {
    const normalizedSnapshot = {
      ...snapshot,
      plan: {
        ...snapshot?.plan,
        meals: (Array.isArray(snapshot?.plan?.meals) ? snapshot.plan.meals : []).map(
          (meal) => ({
            ...meal,
            iconFontFamily: assets?.iconFontFamily,
          }),
        ),
      },
    };

    if (sectionIndex === 0) {
      return buildSinglePlanContent(normalizedSnapshot, assets);
    }

    return buildLaterPlanIntroContent(normalizedSnapshot, sectionIndex);
  });
}

function buildImagesMap(assets = {}) {
  const images = {};

  if (assets?.coachLogoImage) {
    images.coachLogo = assets.coachLogoImage;
  }

  if (assets?.efortLogoImage) {
    images.efortLogo = assets.efortLogoImage;
  }

  return images;
}

export function buildNutritionPlansPdfDefinition(snapshots, assets = {}) {
  const normalizedSnapshots = (Array.isArray(snapshots) ? snapshots : []).filter(Boolean);
  const primarySnapshot = normalizedSnapshots[0] ?? {};
  const images = buildImagesMap(assets);

  return {
    pageSize: "A4",
    pageMargins: PAGE_MARGINS,
    info: {
      title:
        normalizedSnapshots.length === 1
          ? primarySnapshot?.title || getPdfMetadataFallbackTitle(primarySnapshot)
          : getPdfMetadataFallbackTitle(primarySnapshot, { plural: true }),
      author: primarySnapshot?.branding?.coachName || "Efort",
      subject:
        normalizedSnapshots.length === 1
          ? primarySnapshot?.title || getPdfMetadataFallbackTitle(primarySnapshot)
          : getPdfMetadataFallbackTitle(primarySnapshot, { plural: true }),
      creator: "Efort",
      producer: "Efort",
    },
    images,
    header: () => buildHeader(primarySnapshot, assets),
    footer: (currentPage, pageCount) =>
      buildFooter(primarySnapshot, currentPage, pageCount),
    defaultStyle: {
      font: "Roboto",
      fontSize: 10,
      color: Colors.blackColor,
      lineHeight: 1.35,
    },
    styles: {
      documentTitle: {
        fontSize: 16,
        bold: true,
        color: Colors.blackColor,
        alignment: "center",
      },
      headerIdentityLabel: {
        fontSize: 10,
        color: Colors.darkgrayColor,
        alignment: "center",
      },
      headerIdentityValue: {
        fontSize: 10,
        bold: true,
        color: Colors.blackColor,
        alignment: "center",
      },
      footerText: {
        fontSize: 8,
        color: Colors.darkgrayColor,
      },
      footerTextRight: {
        fontSize: 8,
        color: Colors.darkgrayColor,
        alignment: "right",
      },
      footerTextCenter: {
        fontSize: 8,
        color: Colors.darkgrayColor,
        alignment: "center",
      },
      efortWordmark: {
        fontSize: 13,
        bold: true,
        color: Colors.darkBubbleColor,
        alignment: "right",
      },
      notesText: {
        fontSize: 10,
        color: Colors.darkgrayColor,
        lineHeight: 1.4,
        italics: true,
      },
      mealTitle: {
        fontSize: 11,
        bold: true,
        color: Colors.blackColor,
        lineHeight: 1,
      },
      optionTitle: {
        fontSize: 8,
        bold: true,
        color: Colors.darkBubbleColor,
      },
      foodName: {
        fontSize: 10,
        color: Colors.blackColor,
        lineHeight: 1.35,
      },
      foodQuantityInline: {
        fontSize: 9,
        color: Colors.darkgrayColor,
      },
      choiceOptionQuantity: {
        fontSize: 9,
        color: Colors.darkgrayColor,
      },
      choiceOptionName: {
        fontSize: 10,
        color: Colors.blackColor,
      },
    },
    content: buildCombinedPlanContent(normalizedSnapshots, assets),
  };
}

export function buildNutritionPlanPdfDefinition(snapshot, assets = {}) {
  const images = buildImagesMap(assets);

  return {
    pageSize: "A4",
    pageMargins: PAGE_MARGINS,
    info: {
      title: snapshot?.title || getPdfMetadataFallbackTitle(snapshot),
      author: snapshot?.branding?.coachName || "Efort",
      subject: snapshot?.title || getPdfMetadataFallbackTitle(snapshot),
      creator: "Efort",
      producer: "Efort",
    },
    images,
    header: () => buildHeader(snapshot, assets),
    footer: (currentPage, pageCount) => buildFooter(snapshot, currentPage, pageCount),
    defaultStyle: {
      font: "Roboto",
      fontSize: 10,
      color: Colors.blackColor,
      lineHeight: 1.35,
    },
    styles: buildNutritionPlansPdfDefinition([snapshot], assets).styles,
    content: buildSinglePlanContent(
      {
        ...snapshot,
        plan: {
          ...snapshot?.plan,
          meals: (Array.isArray(snapshot?.plan?.meals) ? snapshot.plan.meals : []).map(
            (meal) => ({
              ...meal,
              iconFontFamily: assets?.iconFontFamily,
            }),
          ),
        },
      },
      assets,
    ),
  };
}
